import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Employee, 
  EmployeeFilterParams, 
  PaginationParams 
} from '@/types';
import { 
  useFetch, 
  usePaginatedFetch 
} from './useApi';
import { apiClient } from '@/lib/api';

// Query keys
const EMPLOYEES_KEY = 'employees';

// ==================== Employees ====================

export function useEmployees(params?: EmployeeFilterParams & PaginationParams) {
  return usePaginatedFetch<{ data: Employee[]; meta: { total: number } }>(
    EMPLOYEES_KEY,
    '/payroll/employees',
    {
      page: params?.page || 1,
      limit: params?.limit || 20,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    },
    {
      status: params?.status,
      type: params?.type,
      department: params?.department,
    }
  );
}

export function useEmployee(id: string) {
  return useFetch<Employee>(
    [EMPLOYEES_KEY, id],
    `/payroll/employees/${id}`,
    undefined,
    { enabled: !!id }
  );
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();
  
  return useMutation<Employee, Error, Partial<Employee>>({
    mutationFn: (data) => apiClient.post('/payroll/employees', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EMPLOYEES_KEY] });
    },
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();
  
  return useMutation<Employee, Error, { id: string; data: Partial<Employee> }>({
    mutationFn: ({ id, data }) => apiClient.put(`/payroll/employees/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [EMPLOYEES_KEY] });
      queryClient.invalidateQueries({ queryKey: [EMPLOYEES_KEY, variables.id] });
    },
  });
}

export function useTerminateEmployee() {
  const queryClient = useQueryClient();
  
  return useMutation<Employee, Error, { id: string; endDate: string; reason?: string }>({
    mutationFn: ({ id, endDate, reason }) => 
      apiClient.patch(`/payroll/employees/${id}/terminate`, { endDate, reason }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [EMPLOYEES_KEY] });
      queryClient.invalidateQueries({ queryKey: [EMPLOYEES_KEY, variables.id] });
    },
  });
}

export function useReactivateEmployee() {
  const queryClient = useQueryClient();
  
  return useMutation<Employee, Error, string>({
    mutationFn: (id) => apiClient.patch(`/payroll/employees/${id}/reactivate`, {}),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [EMPLOYEES_KEY] });
      queryClient.invalidateQueries({ queryKey: [EMPLOYEES_KEY, id] });
    },
  });
}
